import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { AlertTriangle, Shield, Plus, Trash2, Pencil, X, ChevronDown, ChevronRight, Search, Layers, MapPin } from 'lucide-react';
import { ConfirmModal } from '../../components/ConfirmModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const NIVEL_COLORS = { BAJO: 'bg-green-500', MEDIO: 'bg-yellow-500', ALTO: 'bg-orange-500', CRITICO: 'bg-red-500' };

export default function CatalogoRiesgos() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [riesgos, setRiesgos] = useState([]);
  const [controles, setControles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterArea, setFilterArea] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [search, setSearch] = useState('');
  const [openNodes, setOpenNodes] = useState({});
  const [showRiesgoModal, setShowRiesgoModal] = useState(false);
  const [showControlModal, setShowControlModal] = useState(null);
  const [editRiesgo, setEditRiesgo] = useState(null);
  const [editControl, setEditControl] = useState(null);
  const [presetArea, setPresetArea] = useState(null);
  const [presetSector, setPresetSector] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, cRes, aRes, sRes] = await Promise.all([
        axios.get(`${API}/riesgos`, { headers }),
        axios.get(`${API}/controles`, { headers }),
        axios.get(`${API}/areas`, { headers }),
        axios.get(`${API}/sectores`, { headers }),
      ]);
      setRiesgos(rRes.data);
      setControles(cRes.data);
      setAreas(aRes.data);
      setSectores(sRes.data);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getControles = (riesgoId) => controles.filter(c => c.riesgo_id === riesgoId);
  const getAreaName = (id) => areas.find(a => a.id === id)?.nombre || 'Sin Area';
  const toggle = (key) => setOpenNodes(p => ({ ...p, [key]: !p[key] }));
  const isOpen = (key) => !!openNodes[key];

  // Build tree: Start from ALL areas/sectores, then attach riesgos
  const buildTree = () => {
    let filtered = riesgos;
    if (filterArea) filtered = filtered.filter(r => r.area_id === filterArea);
    if (filterSector) filtered = filtered.filter(r => r.sector_id === filterSector);
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(r =>
        r.nombre.toLowerCase().includes(q) || r.descripcion.toLowerCase().includes(q) ||
        (r.sector_nombre || '').toLowerCase().includes(q) ||
        getControles(r.id).some(c => c.nombre.toLowerCase().includes(q))
      );
    }

    // Start from all areas (or filtered area/sector)
    let sourceAreas = filterArea ? areas.filter(a => a.id === filterArea) : areas;
    if (filterSector && !filterArea) {
      const sec = sectores.find(s => s.id === filterSector);
      if (sec) sourceAreas = areas.filter(a => a.id === sec.area_id);
    }
    const tree = sourceAreas.map(a => {
      let areaSectores = sectores.filter(s => s.area_id === a.id && s.activo !== false);
      if (filterSector) areaSectores = areaSectores.filter(s => s.id === filterSector);
      const areaRiesgos = filtered.filter(r => r.area_id === a.id);

      // Group risks by sector
      const sectorNodes = areaSectores.map(s => ({
        id: s.id, nombre: s.nombre,
        riesgos: areaRiesgos.filter(r => r.sector_id === s.id)
      }));
      // Risks without sector (general)
      const generalRiesgos = areaRiesgos.filter(r => !r.sector_id || !areaSectores.some(s => s.id === r.sector_id));

      const allSectors = [];
      if (generalRiesgos.length > 0 || !search) {
        allSectors.push({ id: '_general', nombre: 'General', riesgos: generalRiesgos });
      }
      sectorNodes.forEach(s => {
        if (s.riesgos.length > 0 || !search) allSectors.push(s);
      });

      return { id: a.id, nombre: a.nombre, sectors: allSectors };
    });

    // If searching, only show areas that have matching results or sectors
    if (search) return tree.filter(a => a.sectors.some(s => s.riesgos.length > 0));
    return tree;
  };

  const treeData = buildTree();
  const totalRiesgos = treeData.reduce((s, a) => s + a.sectors.reduce((ss, sec) => ss + sec.riesgos.length, 0), 0);

  const expandAll = () => {
    const all = {};
    treeData.forEach(a => {
      all[`area-${a.id}`] = true;
      a.sectors.forEach(s => { all[`sec-${a.id}-${s.id}`] = true; });
    });
    setOpenNodes(p => ({ ...p, ...all }));
  };
  const collapseAll = () => setOpenNodes({});

  // CRUD
  const saveRiesgo = async (data) => {
    try {
      if (editRiesgo) {
        await axios.put(`${API}/riesgos/${editRiesgo.id}`, data, { headers });
        toast.success('Riesgo actualizado');
      } else {
        await axios.post(`${API}/riesgos`, data, { headers });
        toast.success('Riesgo creado');
      }
      fetchAll(); setShowRiesgoModal(false); setEditRiesgo(null); setPresetArea(null); setPresetSector(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteRiesgo = async (id) => {
    setConfirmDelete({ title: 'Eliminar riesgo', message: 'Esta seguro de eliminar este riesgo y sus controles asociados?', onConfirm: async () => {
      setConfirmDelete(null);
      try { await axios.delete(`${API}/riesgos/${id}`, { headers }); toast.success('Eliminado'); fetchAll(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    }});
  };
  const saveControl = async (data) => {
    try {
      if (editControl) {
        await axios.put(`${API}/controles/${editControl.id}`, data, { headers });
        toast.success('Control actualizado');
      } else {
        await axios.post(`${API}/controles`, data, { headers });
        toast.success('Control creado');
      }
      fetchAll(); setShowControlModal(null); setEditControl(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteControl = async (id) => {
    setConfirmDelete({ title: 'Eliminar control', message: 'Esta seguro de eliminar este control?', onConfirm: async () => {
      setConfirmDelete(null);
      try { await axios.delete(`${API}/controles/${id}`, { headers }); toast.success('Eliminado'); fetchAll(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    }});
  };

  const areasConRiesgos = [...new Set(riesgos.map(r => r.area_id).filter(Boolean))];
  const areasFilter = areas.filter(a => areasConRiesgos.includes(a.id));

  if (loading) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B919B] mx-auto" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar riesgo o control..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20"
              data-testid="search-riesgos" />
          </div>
          <select value={filterSector ? `s:${filterSector}` : filterArea ? `a:${filterArea}` : ''}
            onChange={e => {
              const v = e.target.value;
              if (!v) { setFilterArea(''); setFilterSector(''); }
              else if (v.startsWith('a:')) { setFilterArea(v.slice(2)); setFilterSector(''); }
              else if (v.startsWith('s:')) { const sec = sectores.find(s => s.id === v.slice(2)); setFilterSector(v.slice(2)); setFilterArea(sec?.area_id || ''); }
            }}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-area-riesgos">
            <option value="">Todas las areas</option>
            {areas.map(a => {
              const aSectores = sectores.filter(s => s.area_id === a.id && s.activo !== false);
              return (
                <optgroup key={a.id} label={a.nombre}>
                  <option value={`a:${a.id}`}>{a.nombre} (toda el area)</option>
                  {aSectores.map(s => <option key={s.id} value={`s:${s.id}`}>&nbsp;&nbsp;{s.nombre}</option>)}
                </optgroup>
              );
            })}
          </select>
          <span className="text-xs text-slate-400 whitespace-nowrap">{totalRiesgos} riesgo(s) | {controles.length} control(es)</span>
        </div>
        <button onClick={() => { setEditRiesgo(null); setPresetArea(null); setPresetSector(null); setShowRiesgoModal(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#a05e49] text-white text-sm rounded-lg hover:bg-[#8b4f3d] transition-colors whitespace-nowrap"
          data-testid="btn-nuevo-riesgo">
          <Plus className="w-4 h-4" /> Nuevo Riesgo
        </button>
      </div>

      <div className="flex gap-2">
        <button onClick={expandAll} className="text-[10px] text-[#1B919B] hover:underline">Expandir todo</button>
        <span className="text-[10px] text-slate-300">|</span>
        <button onClick={collapseAll} className="text-[10px] text-slate-400 hover:underline">Colapsar todo</button>
      </div>

      {/* Tree */}
      {treeData.length === 0 ? (
        <div className="py-12 text-center text-sm text-slate-400">
          {search || filterArea ? 'No se encontraron resultados' : 'No hay riesgos registrados.'}
        </div>
      ) : (
        <div className="space-y-2">
          {treeData.map(area => {
            const aKey = `area-${area.id}`;
            const aOpen = isOpen(aKey);
            const totalAreaRiesgos = area.sectors.reduce((s, sec) => s + sec.riesgos.length, 0);
            return (
              <div key={area.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`area-group-${area.id}`}>
                {/* AREA */}
                <button onClick={() => toggle(aKey)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-[#1B919B]/5 hover:bg-[#1B919B]/10 transition-colors text-left">
                  <div className="flex items-center gap-2">
                    {aOpen ? <ChevronDown className="w-4 h-4 text-[#1B919B]" /> : <ChevronRight className="w-4 h-4 text-[#1B919B]" />}
                    <Layers className="w-4 h-4 text-[#1B919B]" />
                    <span className="text-sm font-semibold text-[#1B919B]">{area.nombre}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] bg-[#1B919B]/10 text-[#1B919B] px-2 py-0.5 rounded-full font-medium">{totalAreaRiesgos} riesgo(s)</span>
                    <button onClick={e => { e.stopPropagation(); setPresetArea(area.id === '_sin_area' ? null : area.id); setPresetSector(null); setEditRiesgo(null); setShowRiesgoModal(true); }}
                      className="p-1 text-[#1B919B]/50 hover:text-[#1B919B]" title="Agregar riesgo a esta area"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                </button>

                {aOpen && (
                  <div>
                    {area.sectors.map(sector => {
                      const sKey = `sec-${area.id}-${sector.id}`;
                      const sOpen = isOpen(sKey);
                      return (
                        <div key={sector.id}>
                          {/* SECTOR */}
                          <button onClick={() => toggle(sKey)}
                            className="w-full flex items-center justify-between px-4 py-2 pl-10 bg-amber-50/50 hover:bg-amber-50 transition-colors text-left border-t border-slate-100">
                            <div className="flex items-center gap-2">
                              {sOpen ? <ChevronDown className="w-3.5 h-3.5 text-amber-600" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-600" />}
                              <MapPin className="w-3.5 h-3.5 text-amber-600" />
                              <span className="text-xs font-semibold text-amber-700">{sector.nombre}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">{sector.riesgos.length}</span>
                              {sector.id !== '_general' && (
                                <button onClick={e => { e.stopPropagation(); setPresetArea(area.id === '_sin_area' ? null : area.id); setPresetSector(sector.id); setEditRiesgo(null); setShowRiesgoModal(true); }}
                                  className="p-0.5 text-amber-400 hover:text-amber-600" title="Agregar riesgo a este sector"><Plus className="w-3 h-3" /></button>
                              )}
                            </div>
                          </button>

                          {/* RIESGOS */}
                          {sOpen && sector.riesgos.map(r => {
                            const ctrls = getControles(r.id);
                            const rKey = `riesgo-${r.id}`;
                            const rOpen = openNodes[rKey];
                            return (
                              <div key={r.id} data-testid={`riesgo-card-${r.id}`}>
                                <div className="flex items-center gap-2 px-4 py-2 pl-16 hover:bg-slate-50 cursor-pointer border-t border-slate-50" onClick={() => toggle(rKey)}>
                                  {rOpen ? <ChevronDown className="w-3 h-3 text-orange-500" /> : <ChevronRight className="w-3 h-3 text-orange-500" />}
                                  <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded text-white ${NIVEL_COLORS[r.nivel] || NIVEL_COLORS.MEDIO}`}>{r.nivel || 'MEDIO'}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-800 truncate">{r.nombre}</p>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span className="text-[9px] text-slate-400">{ctrls.length} ctrl</span>
                                    <button onClick={e => { e.stopPropagation(); setEditRiesgo(r); setPresetArea(null); setPresetSector(null); setShowRiesgoModal(true); }}
                                      className="p-0.5 text-slate-300 hover:text-[#1B919B]"><Pencil className="w-2.5 h-2.5" /></button>
                                    <button onClick={e => { e.stopPropagation(); deleteRiesgo(r.id); }}
                                      className="p-0.5 text-slate-300 hover:text-red-500"><Trash2 className="w-2.5 h-2.5" /></button>
                                  </div>
                                </div>

                                {/* CONTROLES */}
                                {rOpen && (
                                  <div className="bg-slate-50/80 px-4 py-1.5 pl-24 space-y-0.5">
                                    <div className="flex items-center justify-between">
                                      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Controles</p>
                                      <button onClick={() => { setEditControl(null); setShowControlModal(r.id); }}
                                        className="flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-[#1B919B] text-white rounded hover:bg-[#167a83]">
                                        <Plus className="w-2 h-2" /> Control
                                      </button>
                                    </div>
                                    {ctrls.length === 0 ? (
                                      <p className="text-[10px] text-slate-300 py-1">Sin controles</p>
                                    ) : ctrls.map(c => (
                                      <div key={c.id} className="flex items-center justify-between py-0.5 group">
                                        <div className="flex items-center gap-1.5">
                                          <Shield className="w-2.5 h-2.5 text-[#1B919B]" />
                                          <span className="text-[11px] text-slate-700">{c.nombre}</span>
                                          <span className="text-[9px] bg-[#1B919B]/10 text-[#1B919B] px-1 py-0.5 rounded">{c.tipo}</span>
                                          {c.sector_nombre && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded">{c.sector_nombre}</span>}
                                        </div>
                                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                                          <button onClick={() => { setEditControl(c); setShowControlModal(r.id); }}
                                            className="p-0.5 text-slate-300 hover:text-[#1B919B]"><Pencil className="w-2.5 h-2.5" /></button>
                                          <button onClick={() => deleteControl(c.id)}
                                            className="p-0.5 text-slate-300 hover:text-red-500"><Trash2 className="w-2.5 h-2.5" /></button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showRiesgoModal && <RiesgoModal areas={areas} sectores={sectores} initial={editRiesgo} presetArea={presetArea} presetSector={presetSector} onSave={saveRiesgo} onClose={() => { setShowRiesgoModal(false); setEditRiesgo(null); setPresetArea(null); setPresetSector(null); }} />}
      {showControlModal && <ControlModal riesgoId={showControlModal} riesgos={riesgos} sectores={sectores} initial={editControl} onSave={saveControl} onClose={() => { setShowControlModal(null); setEditControl(null); }} />}
      <ConfirmModal open={!!confirmDelete} title={confirmDelete?.title || ''} message={confirmDelete?.message || ''}
        confirmLabel="Eliminar" variant="danger" onConfirm={confirmDelete?.onConfirm} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
}

function RiesgoModal({ areas, sectores, initial, presetArea, presetSector, onSave, onClose }) {
  const [form, setForm] = useState({
    nombre: initial?.nombre || '', descripcion: initial?.descripcion || '',
    area_id: initial?.area_id || presetArea || '', sector_id: initial?.sector_id || presetSector || '',
    nivel: initial?.nivel || 'MEDIO'
  });
  const sectoresDelArea = form.area_id ? sectores.filter(s => s.area_id === form.area_id) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="modal-riesgo">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">{initial ? 'Editar' : 'Nuevo'} Riesgo</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Nombre *</label>
            <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              placeholder="Ej: Caidas de altura" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-riesgo-nombre" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Descripcion *</label>
            <textarea value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              placeholder="Descripcion detallada" rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Area *</label>
              <select value={form.area_id} onChange={e => setForm(f => ({ ...f, area_id: e.target.value, sector_id: '' }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="select-riesgo-area">
                <option value="">Seleccione</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Sector</label>
              <select value={form.sector_id} onChange={e => setForm(f => ({ ...f, sector_id: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="select-riesgo-sector">
                <option value="">General (toda el area)</option>
                {sectoresDelArea.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
              {!form.area_id && <p className="text-[9px] text-slate-400 mt-0.5">Seleccione area primero</p>}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Nivel *</label>
            <div className="flex gap-2">
              {['BAJO', 'MEDIO', 'ALTO', 'CRITICO'].map(n => (
                <button key={n} type="button" onClick={() => setForm(f => ({ ...f, nivel: n }))}
                  className={`flex-1 py-1.5 rounded text-[10px] font-bold transition-colors ${form.nivel === n ? `${NIVEL_COLORS[n]} text-white` : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}>{n}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">Cancelar</button>
          <button onClick={() => { if (!form.nombre || !form.descripcion || !form.area_id) { toast.error('Complete nombre, descripcion y area'); return; } onSave({ ...form, sector_id: form.sector_id || null }); }}
            className="px-4 py-2 text-sm bg-[#a05e49] text-white rounded-lg hover:bg-[#8b4f3d]" data-testid="btn-guardar-riesgo">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function ControlModal({ riesgoId, riesgos, sectores, initial, onSave, onClose }) {
  const riesgo = riesgos.find(r => r.id === riesgoId);
  const areaId = riesgo?.area_id;
  const sectoresDelArea = areaId ? sectores.filter(s => s.area_id === areaId) : sectores;
  const [form, setForm] = useState({
    nombre: initial?.nombre || '', tipo: initial?.tipo || 'EPP',
    riesgo_id: riesgoId, sector_id: initial?.sector_id || ''
  });
  const TIPOS = ['EPP', 'Ingenieria', 'Administrativo', 'Capacitacion', 'Senalizacion', 'Eliminacion', 'Sustitucion'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="modal-control">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">{initial ? 'Editar' : 'Nuevo'} Control</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Nombre *</label>
            <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              placeholder="Ej: Uso de arnes" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-control-nombre" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Tipo *</label>
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="select-control-tipo">
                {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Sector</label>
              <select value={form.sector_id} onChange={e => setForm(f => ({ ...f, sector_id: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="select-control-sector">
                <option value="">General</option>
                {sectoresDelArea.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">Cancelar</button>
          <button onClick={() => { if (!form.nombre) { toast.error('Ingrese nombre'); return; } onSave({ ...form, sector_id: form.sector_id || null }); }}
            className="px-4 py-2 text-sm bg-[#1B919B] text-white rounded-lg hover:bg-[#167a83]" data-testid="btn-guardar-control">Guardar</button>
        </div>
      </div>
    </div>
  );
}
